/**
 * Propuesta mensual de la IA para la ficha de una solución (03/11/2026). La ve solo quien puede
 * editar la ficha (dueño o roster): cada sección propuesta con lo que hay hoy al lado y el motivo,
 * con un tilde por sección. "Aplicar" guarda las elegidas (con historial); "Descartar" la cierra.
 * La IA nunca publica sola.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useDecideOrgSheetProposal, useOrgSheetProposal } from '@/hooks/useOrg';
import { toastError } from '@/lib/toast-error';
import type { Section } from '@/../../shared/section-content';

const k = 'org.sheet.proposal';

function SectionPreview({ s }: { s: Section | null }) {
  const { t } = useTranslation();
  if (!s) return <p className="text-xs italic text-muted-foreground">{t(`${k}.new_section`, 'Sección nueva')}</p>;
  return (
    <div className="space-y-1 text-xs">
      {s.intro && <p className="whitespace-pre-line text-muted-foreground">{s.intro}</p>}
      <ul className="list-disc pl-4">{s.items.map((i, j) => <li key={j}><span className="font-medium">{i.title}</span>{i.description ? `: ${i.description}` : ''}</li>)}</ul>
      {!s.intro && !s.items.length && <p className="italic text-muted-foreground">{t(`${k}.empty`, 'Vacía')}</p>}
    </div>
  );
}

export function OrgSheetProposal({ slug, enabled }: { slug: string; enabled: boolean }) {
  const { t } = useTranslation();
  const q = useOrgSheetProposal(slug, enabled);
  const { apply, discard } = useDecideOrgSheetProposal(slug);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const p = q.data;
  if (!enabled || !p) return null;
  // Solo ids de ESTA propuesta (si llegó una nueva con el diálogo montado, lo elegido antes no vale).
  const valid = new Set(p.sections.map((s) => s.sectionId));
  const selected = picked ? new Set([...picked].filter((id) => valid.has(id))) : valid;
  const busy = apply.isPending || discard.isPending;
  const toggle = (id: string) => setPicked(() => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertTitle>{p.status === 'stale' ? t(`${k}.stale_title`, 'Hay una propuesta vieja') : t(`${k}.title`, 'La IA propone cambios en {{n}} secciones', { n: p.sections.length })}</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
          <span>{p.status === 'stale' ? t(`${k}.stale`, 'La ficha cambió después de la propuesta: podés descartarla; la próxima sale el mes que viene.') : t(`${k}.desc`, 'Salen de los puestos, herramientas y clientes reales de la solución. Vos decidís qué entra.')}</span>
          <span className="flex gap-2">
            {p.status !== 'stale' && <Button size="sm" onClick={() => setOpen(true)}>{t(`${k}.review`, 'Revisar')}</Button>}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => discard.mutate({ slug }, { onSuccess: () => toast.success(t(`${k}.discarded`, 'Propuesta descartada')), onError: (e) => toastError(e, t(`${k}.failed`, 'No se pudo guardar la decisión')) })}>{t(`${k}.discard`, 'Descartar')}</Button>
          </span>
        </AlertDescription>
      </Alert>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader><DialogTitle>{t(`${k}.dialog_title`, 'Propuesta de la IA ({{period}})', { period: p.period })}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {p.sections.map((s) => (
              <div key={s.sectionId} className="rounded-lg border p-3 space-y-2">
                <label className="flex items-center gap-2 font-medium">
                  <Checkbox checked={selected.has(s.sectionId)} onCheckedChange={() => toggle(s.sectionId)} />
                  {s.after.title}
                </label>
                {s.reason && <p className="text-xs text-muted-foreground">{t(`${k}.reason`, 'Por qué: {{reason}}', { reason: s.reason })}</p>}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><p className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">{t(`${k}.before`, 'Hoy')}</p><SectionPreview s={s.before} /></div>
                  <div><p className="mb-1 text-[10px] font-semibold uppercase text-brand-green">{t(`${k}.after`, 'Propuesta')}</p><SectionPreview s={s.after} /></div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
            <Button disabled={busy || selected.size === 0} onClick={() => apply.mutate({ slug, acceptedSectionIds: [...selected] }, {
              onSuccess: () => { toast.success(t(`${k}.applied`, 'Ficha actualizada con la propuesta')); setOpen(false); },
              onError: (e) => toastError(e, t(`${k}.failed`, 'No se pudo guardar la decisión')),
            })}>
              {apply.isPending ? <WinclapLoader size={14} /> : t(`${k}.apply`, 'Aplicar {{n}} secciones', { n: selected.size })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
